import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Tags, Flag, Mail, LayoutGrid, Trophy, Store, Users, ShieldCheck, LogOut, ChevronRight, Palette, Scale, ListChecks, CreditCard, DatabaseBackup, CalendarRange, BookOpen, ShoppingCart } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth, ROLE_INFO } from "@/context/AuthContext";
import { useUnit } from "@/context/UnitContext";
import { Badge, Button, Card, Page, PageHeader } from "@/components/kit";

function Row({ icon: Icon, label, value, onClick, testid, tone }) {
  return (
    <div className="row-link" onClick={onClick} data-testid={testid}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--brand-soft)" }}><Icon size={18} style={{ color: "var(--brand)" }} /></div>
      <span className="h3 flex-1">{label}</span>
      {value && <Badge tone={tone || "muted"}>{value}</Badge>}
      <ChevronRight size={17} className="muted" />
    </div>
  );
}

export default function Menu() {
  const nav = useNavigate();
  const { user, isMgmt, isAdmin, logout } = useAuth();
  const { unit } = useUnit();
  const [info, setInfo] = useState(false);
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => api("/users"), enabled: isAdmin });
  const pending = users.filter((u) => !u.approved).length;
  return (
    <Page narrow>
      <PageHeader title="Menu" />
      <Card className="flex items-center gap-4 mb-5" data-testid="profile-card">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-extrabold" style={{ background: "var(--brand)" }}>{user.name[0]}</div>
        <div className="flex-1 min-w-0">
          <div className="h2 truncate">{user.name}</div>
          <div className="caption truncate">{user.email}</div>
          <div className="mt-1 flex gap-2"><Badge>{ROLE_INFO[user.role].label}</Badge>{unit && <Badge tone="muted">{unit.name}</Badge>}</div>
        </div>
      </Card>
      <button type="button" onClick={() => nav("/tutorial")} className="w-full mb-5 rounded-2xl overflow-hidden flex items-center text-left border transition-transform duration-200 hover:-translate-y-0.5" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="menu-tutorial">
        <img src="/tutorial/il-start.webp" alt="" className="w-24 sm:w-32 h-20 object-cover shrink-0" />
        <span className="flex-1 px-4"><span className="label block">Tutorial passo a passo</span><span className="caption">Aprenda a usar o sistema com as telas reais</span></span>
        <ChevronRight size={18} className="mr-4" style={{ color: "var(--muted)" }} />
      </button>
      {isMgmt && (
        <>
          <div className="eyebrow mb-2 ml-1">Cadastros</div>
          <div className="card divide mb-5" style={{ padding: 0 }}>
            <Row icon={Tags} label="Categorias" onClick={() => nav("/categories")} testid="menu-categories" />
            <Row icon={BookOpen} label="Categorias de fichas técnicas" onClick={() => nav("/menu-categories")} testid="menu-menu-categories" />
            <Row icon={LayoutGrid} label="Setores" onClick={() => nav("/sectors")} testid="menu-sectors" />
            <Row icon={Trophy} label="Fornecedores" onClick={() => nav("/suppliers")} testid="menu-suppliers" />
            <Row icon={Store} label="Unidades" onClick={() => nav("/units")} testid="menu-units" />
            {isAdmin && <Row icon={Users} label="Usuários" value={pending ? `${pending} pendente${pending > 1 ? "s" : ""}` : null} tone="warning" onClick={() => nav("/users")} testid="menu-users" />}
            <Row icon={ListChecks} label="Tarefas da equipe" onClick={() => nav("/tasks")} testid="menu-tasks" />
            {isAdmin && <Row icon={CreditCard} label="Assinatura" onClick={() => nav("/billing")} testid="menu-billing" />}
            {isAdmin && <Row icon={DatabaseBackup} label="Backup dos dados" onClick={() => nav("/backup")} testid="menu-backup" />}
          </div>
          <div className="eyebrow mb-2 ml-1">Configurações</div>
          <div className="card divide mb-5" style={{ padding: 0 }}>
            <Row icon={Flag} label="Metas e alertas" onClick={() => nav("/cmv-target")} testid="menu-cmv-target" />
            <Row icon={CalendarRange} label="Período das conferências" onClick={() => nav("/conference-period")} testid="menu-conference-period" />
            <Row icon={Scale} label="CMV teórico x real" onClick={() => nav("/cmv-analysis")} testid="menu-cmv-analysis" />
                       <Row icon={Mail} label="Envio automático" onClick={() => nav("/report-emails")} testid="menu-report-emails" />
            {isAdmin && <Row icon={Palette} label="Personalizar layout" onClick={() => nav("/layout")} testid="menu-layout" />}
          </div>
        </>
      )}
      <div className="card divide mb-5" style={{ padding: 0 }}>
        <Row icon={ShoppingCart} label="Compras" onClick={() => nav("/shopping-lists")} testid="menu-shopping-lists" />
 <Row icon={ShieldCheck} label="Níveis de acesso" onClick={() => setInfo(!info)} testid="menu-access-levels" />
        {info && (
          <div className="flex flex-col gap-3" style={{ padding: "var(--pad)" }} data-testid="access-levels-info">
            {Object.entries(ROLE_INFO).map(([k, r]) => (
              <div key={k} className="rounded-xl p-3" style={{ background: k === user.role ? "var(--brand-soft)" : "var(--surface-alt)" }}>
                <div className="label">{r.label}</div><div className="caption">{r.desc}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Button variant="danger" icon={LogOut} className="w-full" onClick={logout} data-testid="logout-button">Sair</Button>
    </Page>
  );
}
